import {Component, inject, ViewEncapsulation, ChangeDetectionStrategy} from '@angular/core';
import {VwuiModalConfig, VwuiModalRef} from '@bvgo-ict/vwui-angular';
import {UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {TranslateService} from '@ngx-translate/core';
import {IPossibleAnswerData, IQuestionData, IQuestionType, LibraryEntry} from '../../models';
import {FormUtil} from '../../utils';
import {TrackingService} from '../../services';
import {Question} from '../../models/definition/question';
import {TisCalculationOperator} from '../../models/definition/tis-calculation-operator';
import {ITisCalculation} from '../../models/definition/tis-calculation';
import {ProjectAnalysisDefinition} from "../../models/project-analysis";
import {PitOptions} from "../../config";
import {PossibleAnswer} from "../../models/definition/possibleanswer";
import {INT32_MAX, createIntegerMask, int32RangeValidator} from '../../utils/question-answer.util';

/**
 * Cloned/copied/refactored version of `TisCreateUpdateQuestionModalComponent` specific for project analysis.
 */
@Component({
    selector: 'app-project-analysis-question-modal',
    templateUrl: './project-analysis-question-modal.html',
    styleUrls: ['./project-analysis-question-modal.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ProjectAnalysisQuestionModalComponent {
    public modalRef = inject(VwuiModalRef);
    public modalConfig =
        inject<VwuiModalConfig<{ title: string; data?: IQuestionData; libraries: LibraryEntry[], saveText: string; definition: ProjectAnalysisDefinition; }>>(VwuiModalConfig);
    private translateService = inject(TranslateService);
    private trackingService = inject(TrackingService);
    private formBuilder = inject(UntypedFormBuilder);
    protected readonly IQuestionType = IQuestionType;
    public readonly minimumMultipleChoiceAnswers = 2;

    public readonly maxTextAnswerLength = 16_384;
    public readonly defaultTextAnswerLength = 1_024;
    public form: UntypedFormGroup;
    public loading = false;
    public libraries: { id: number; title: string }[] = [];
    public title: string;
    public saveText: string;
    public data?: IQuestionData;
    public questionTypes: { label: string; value: string }[];
    private definition: ProjectAnalysisDefinition;
    colorScores = [
        {key: 'RED', label: '\u{1F534}'},
        {key: 'ORANGE', label: '\u{1F7E0}'},
        {key: 'GREEN', label: '\u{1F7E2}'},
    ];

    get possibleAnswersFormArray(): UntypedFormArray {
        return this.form.controls.possibleAnswers as UntypedFormArray;
    }

    get isMultipleChoice(): boolean {
        return this.form.value.type === IQuestionType.MULTIPLECHOICE;
    }

    get isCalculation(): boolean {
        return this.form.value.type === IQuestionType.CALCULATION;
    }

    get isNumeric(): boolean {
        return this.form.value.type === IQuestionType.INTEGER || this.form.value.type === IQuestionType.FINANCIAL;
    }

    private integerMaskConfig?: Record<string, unknown>;

    public get integerMask(): Record<string, unknown> {
        if (!this.integerMaskConfig) {
            this.integerMaskConfig = createIntegerMask(1, INT32_MAX);
        }

        return this.integerMaskConfig;
    }

    get isText(): boolean {
        return this.form.value.type === IQuestionType.TEXT;
    }

    public isConcept(): boolean {
        return this.definition?.isConcept;
    }

    /**
     * Gets a list of possible dependent questions, having a label prefixed with category+question index.
     */
    get possibleDependentQuestions(): Question[] {
        const questions: Question[] = [];
        this.definition.categories?.forEach((category, categoryIndex) => {
            category.questions?.forEach((question, questionIndex) => {
                if (question.tempId !== this.form.value.tempId && (
                    question.type === IQuestionType.MULTIPLECHOICE ||
                    question.type === IQuestionType.BOOL)) {
                    question.selectTitle = `${categoryIndex + 1}.${questionIndex + 1}  ${question.title}`;
                    questions.push(question);
                }
            })
        });
        return questions;
    }

    get integerQuestions(): Question[] {
        return this.definition
            .questions
            .filter(q => q.type === IQuestionType.INTEGER || q.type === IQuestionType.FINANCIAL);
    }

    get showDependentValueSelect(): boolean {
        return this.form.value.dependentQuestion.tempId != null;
    }

    get dependentQuestion(): Question | null {
        const tempId = this.form.value.dependentQuestion.tempId as string | null;
        return this.definition
            .questions
            .find(q => q.tempId === tempId) ?? null;
    }

    get dependentQuestionOptions(): PossibleAnswer[] {
        return this.dependentQuestion?.possibleAnswers ?? [];
    }

    get dependentQuestionControl(): UntypedFormGroup {
        return this.form.controls.dependentQuestion as UntypedFormGroup;
    }

    constructor() {
        const modalConfig = this.modalConfig;

        this.title = modalConfig.data.title;
        this.saveText = modalConfig.data.saveText;
        this.data = modalConfig.data.data;
        this.definition = modalConfig.data.definition;
        const nameValue = this.data ? this.data.name : '';
        const supportedQuestionTypes = [
            IQuestionType.TEXT,
            IQuestionType.DATE,
            IQuestionType.INTEGER,
            IQuestionType.FINANCIAL,
            IQuestionType.BOOL,
            IQuestionType.PERCENTAGE,
            IQuestionType.MULTIPLECHOICE,
            IQuestionType.CHECKBOX,
            // Leave these out for now, perhaps not needed?
            //IQuestionType.DEPARTMENT,
            //IQuestionType.CALCULATION
        ];

        const questionTypeValue = this.data && this.data.type ? this.data.type : IQuestionType.TEXT;
        const allowAttachmentsValue = this.data && (this.data.allowAttachments ?? false);
        const isRequiredValue = !!this.data?.isRequired;
        const isQuickScanValue = !!this.data?.isQuickScan;
        const tempIdValue = this.data?.tempId;
        let textAnswerLengthValue = this.data?.textAnswerLength;
        const calculationValue = this.data?.calculation;
        const dependentQuestionValue = this.data.dependentQuestion;
        const pitValue = this.data?.pitTag ?? '';
        const titleValue = this.data?.title;
        const questionValue = this.data?.question;
        const maxValueValue = this.data?.maxValue;
        const minValueValue = this.data?.minValue;
        const defaultValueValue = this.data?.defaultValue;
        const showExplanationValue = this.data?.showExplanation;
        const isMultiAnswerValue = this.data?.isMultiAnswer;
        const infoValue = this.data?.info;

        if (questionTypeValue === IQuestionType.TEXT && textAnswerLengthValue == null) {
            textAnswerLengthValue = this.defaultTextAnswerLength;
        }

        this.questionTypes = supportedQuestionTypes.map((type) => ({
            label: this.translateService.instant('COMPONENT.tis.modal.create_question.type.' + type),
            value: type
        }));

        this.libraries = modalConfig.data.libraries?.map(
            (item) => ({id: item.id, title: item.getTitle()})
        );

        this.form = this.formBuilder.group({
            tempId: [tempIdValue, Validators.required],
            // deprecated, has been superseded by title+question
            name: nameValue,
            type: [questionTypeValue, Validators.required],
            allowAttachments: allowAttachmentsValue,
            isRequired: isRequiredValue,
            isQuickScan: isQuickScanValue,
            textAnswerLength: [textAnswerLengthValue],
            dependentQuestion: this.formBuilder.group({
                tempId: [dependentQuestionValue?.tempId],
                answerTempId: [dependentQuestionValue?.answerTempId, Validators.required],
                boolValue: [dependentQuestionValue?.boolValue, Validators.required]
            }),
            possibleAnswers: this.formBuilder.array([]),
            pitTag: pitValue,
            title: [titleValue, Validators.required],
            question: questionValue,
            maxValue: [maxValueValue, [int32RangeValidator(1, INT32_MAX)]],
            minValue: [minValueValue, [int32RangeValidator(1, INT32_MAX)]],
            defaultValue: defaultValueValue,
            showExplanation: showExplanationValue,
            isMultiAnswer: isMultiAnswerValue,
            info: infoValue,
        });

        this.toggleCalculationControl(questionTypeValue, calculationValue);
        this.updateTextAnswerLengthValidators(questionTypeValue);
        this.toggleDependentQuestionControls(dependentQuestionValue?.tempId ?? null);
        this.populatePossibleAnswerLibraries();

        for (const answer of this.data?.possibleAnswers ?? []) {
            this.addAnswer(answer);
        }

        // only enable these if editing
        if (this.isConcept()) {
            this.form.controls.type.valueChanges.subscribe((newType: IQuestionType) => {
                if (newType === IQuestionType.MULTIPLECHOICE) {
                    for (let i = 0; i < this.minimumMultipleChoiceAnswers; i++) {
                        this.addAnswer();
                    }
                } else {
                    this.possibleAnswersFormArray.clear();
                }

                this.toggleCalculationControl(newType);
                this.updateTextAnswerLengthValidators(newType);
            });

            this.dependentQuestionControl.controls.tempId.valueChanges.subscribe(tempId => {
                this.dependentQuestionControl.patchValue({boolValue: null, answerTempId: null});
                this.toggleDependentQuestionControls(tempId);
            });

        } else {
            Object.keys(this.form.controls).forEach(key => {
               this.form.controls[key].disable();
            });
        }
    }

    private toggleDependentQuestionControls(dependentQuestionTempId: string | null): void {
        const question = this.definition.questions.find(q => q.tempId === dependentQuestionTempId);
        if (question == null) {
            this.dependentQuestionControl.controls.answerTempId.disable();
            this.dependentQuestionControl.controls.boolValue.disable();
        } else if (question.type === IQuestionType.BOOL) {
            this.dependentQuestionControl.controls.answerTempId.disable();
            this.dependentQuestionControl.controls.boolValue.enable();
        } else if (question.type === IQuestionType.MULTIPLECHOICE) {
            this.dependentQuestionControl.controls.answerTempId.enable();
            this.dependentQuestionControl.controls.boolValue.disable();
        }
    }

    // currently not used
    private toggleCalculationControl(type: IQuestionType, initialValue: ITisCalculation | null = null): void {
        const isRequiredControl = this.form.controls.isRequired;
        if (type === IQuestionType.CALCULATION) {
            this.form.addControl('calculation', this.formBuilder.group({
                operator: [initialValue?.operator ?? TisCalculationOperator.SUBTRACT, Validators.required],
                leftQuestionTempId: [initialValue?.leftQuestionTempId ?? null, Validators.required],
                rightQuestionTempId: [initialValue?.rightQuestionTempId ?? null, Validators.required]
            }));
            isRequiredControl.patchValue(false);
            isRequiredControl.disable();
        } else {
            this.form.removeControl('calculation');
            isRequiredControl.enable();
        }
    }

    private updateTextAnswerLengthValidators(newType: IQuestionType): void {
        if (newType === IQuestionType.TEXT) {
            this.form.controls.textAnswerLength.addValidators([Validators.required, Validators.min(1), Validators.max(this.maxTextAnswerLength)]);
        } else {
            this.form.controls.textAnswerLength.clearValidators();
        }
    }

    // copy/pasted code of Quickscan question modal
    private populatePossibleAnswerLibraries() {
        this.form.get('possibleAnswers').valueChanges.subscribe(possibleAnswers => {
            const libraryIdMap = this.libraries.reduce((acc, value) => {
                acc[value.id] = value.title;
                return acc;
            }, {});

            possibleAnswers = possibleAnswers.map((possibleAnswer) => {
                const isLibraryItemNumbers = possibleAnswer.libraryEntryIds?.length > 0 && typeof possibleAnswer.libraryEntryIds[0] === 'number';
                if (isLibraryItemNumbers) {
                    possibleAnswer.libraryEntryIds.sort((libraryEntry1, libraryEntry2) => {
                        // Different sorting because of mixed types
                        if (typeof libraryEntry1 === 'number') {
                            if (libraryIdMap[libraryEntry1] > libraryIdMap[libraryEntry2]) {
                                return 1;
                            } else if (libraryIdMap[libraryEntry1] < libraryIdMap[libraryEntry2]) {
                                return -1;
                            }
                        } else {
                            if (libraryIdMap[libraryEntry1.id] > libraryIdMap[libraryEntry2.id]) {
                                return 1;
                            } else if (libraryIdMap[libraryEntry1.id] < libraryIdMap[libraryEntry2.id]) {
                                return -1;
                            }
                        }

                        return 0;
                    });
                    possibleAnswer.libraryEntryIds = possibleAnswer.libraryEntryIds.map((libraryEntryId) =>
                        // observe that the id is replaced by an object with id + title
                        ({id: libraryEntryId, title: libraryIdMap[libraryEntryId]})
                    );
                }

                return possibleAnswer;
            });

            this.form.controls.possibleAnswers.setValue(possibleAnswers, {onlySelf: true, emitEvent: false});
        });
    }

    public submit(): void {
        try {
            FormUtil.validate(this.form);

            if (this.form.valid) {
                // translate libraryEntryIds back to id list
                let possibleAnswersValue = Object.assign([], this.form.get('possibleAnswers').value);
                possibleAnswersValue = possibleAnswersValue.map((possibleAnswer) => {
                    if (possibleAnswer.libraryEntryIds) {
                        possibleAnswer.libraryEntryIds = possibleAnswer.libraryEntryIds.map((libraryEntryId) => libraryEntryId.id);
                    }
                    return possibleAnswer;
                });
                this.form.controls.possibleAnswers.setValue(possibleAnswersValue, {onlySelf: false, emitEvent: false});

                this.modalRef.close({...this.data, ...this.form.value});
            }

        } catch (e) {
            console.error('Create/update question failed: ', e);
            this.trackingService.exception(e);
        }
    }

    public addAnswer(answer: IPossibleAnswerData | null = null): void {
        this.possibleAnswersFormArray.push(this.formBuilder.group({
            tempId: [answer?.tempId ?? crypto.randomUUID(), Validators.required],
            id: [answer?.id],
            answer: [answer?.answer ?? '', Validators.required],
            colorScore: [answer?.colorScore],
            score: [answer?.score],
            libraryEntryIds: [answer?.libraryEntryIds],
        }));
    }

    public removeAnswer(index: number): void {
        this.possibleAnswersFormArray.removeAt(index);
    }

    public pitOptions() {
        return PitOptions.getInstance().getOptions();
    }

    // for sorting the library select items
    public compareEntry(a: { id: number }, b: { id: number }): boolean {
        return !!a && !!b && a.id === b.id;
    }
}
